import React, { useCallback, useEffect, useMemo, useState } from "react";
import Seo, { pageSeo } from "../components/Seo";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Search, Trash2, Mail, Phone, Inbox, CheckCircle2, Clock, Loader2, MailWarning, FileSpreadsheet, MailCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Logo } from "../components/Header";
import { company } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "anjaneya_admin_token";

const statusMeta = {
  new: { label: "New", cls: "bg-[#e8f5d5] text-[#4f8a12] border-[#c9e6a1]" },
  contacted: { label: "Contacted", cls: "bg-[#e6f3fa] text-[#1c6f9c] border-[#bfe0f2]" },
  closed: { label: "Closed", cls: "bg-[#f1f1f1] text-[#58595b] border-[#dcdcdc]" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Login = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f4f4] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md bg-white rounded-md shadow-lg border border-[#ececec] p-8">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="flex items-center gap-2 text-[#231f20]">
          <span className="w-10 h-10 rounded-full bg-[#8cc63f]/15 text-[#5f9a17] flex items-center justify-center"><Lock size={18} /></span>
          <div>
            <h1 className="text-[20px] font-bold leading-tight">Enquiry Dashboard</h1>
            <p className="text-[13px] text-[#6d6e71]">Private area - enter the admin password</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="field field--light"
            autoFocus
            data-testid="admin-password"
          />
          {error && <p className="text-[#c0392b] text-[13px]" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading || !password} className="btn-green w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white border border-[#ececec] rounded-md p-5 flex items-center gap-4 shadow-sm" data-testid={`stat-${label.toLowerCase()}`}>
    <span className={`w-11 h-11 rounded-full flex items-center justify-center ${tone}`}><Icon size={20} /></span>
    <div>
      <div className="text-[26px] font-black text-[#231f20] leading-none">{value}</div>
      <div className="text-[12px] uppercase tracking-wider text-[#6d6e71] mt-1">{label}</div>
    </div>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailInfo, setEmailInfo] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);

  const client = useMemo(() => axios.create({ baseURL: API, headers: { "X-Admin-Token": token } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? { limit: 500 } : { limit: 500, status: filter };
      const [list, st, ver] = await Promise.all([
        client.get("/enquiries", { params }),
        client.get("/enquiries/stats"),
        client.get("/admin/verify"),
      ]);
      setItems(list.data);
      setStats(st.data);
      setEmailConfigured(!!ver.data.email_configured);
      setEmailInfo(ver.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [client, filter, onLogout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await client.patch(`/enquiries/${id}`, { status });
      setItems((list) => list.map((i) => (i.id === id ? data : i)));
      const st = await client.get("/enquiries/stats");
      setStats(st.data);
      toast.success(`Marked as ${statusMeta[status].label}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await client.delete(`/enquiries/${toDelete.id}`);
      setItems((list) => list.filter((i) => i.id !== toDelete.id));
      const st = await client.get("/enquiries/stats");
      setStats(st.data);
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    } finally {
      setToDelete(null);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await client.get("/enquiries/export", { params, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "anjaneya-enquiries.xlsx";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded", { description: filename });
    } catch {
      toast.error("Could not export enquiries");
    } finally {
      setExporting(false);
    }
  };

  const visible = items.filter((i) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [i.name, i.email, i.phone, i.company, i.message, i.product, i.shade].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-[#f4f4f4]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#ececec] sticky top-0 z-30">
        <div className="container-dic h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden md:inline-block text-[12px] uppercase tracking-wider font-bold text-[#6d6e71] border-l border-[#e5e5e5] pl-4">Enquiry Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={exportExcel} disabled={exporting} className="btn-green !py-2 !px-4 text-[13px] disabled:opacity-60" data-testid="admin-export">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
              <span className="hidden sm:inline">Export Excel</span>
            </button>
            <button type="button" onClick={load} className="p-2 rounded text-[#4d4d4f] hover:text-[#7ab929] hover:bg-[#f4f9ea] transition-colors" aria-label="Refresh" data-testid="admin-refresh">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button type="button" onClick={onLogout} className="inline-flex items-center gap-2 text-[13px] font-bold text-[#4d4d4f] hover:text-[#c0392b] px-3 py-2 transition-colors" data-testid="admin-logout">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-dic py-8">
        {emailConfigured ? (
          <div className="mb-6 flex items-start gap-3 bg-[#f1f8e8] border border-[#cfe6b3] text-[#3f6b0f] rounded-md px-4 py-3 text-[13.5px]" data-testid="email-configured">
            <MailCheck size={18} className="mt-0.5 shrink-0" />
            <p>
              Email alerts go to <strong>{emailInfo.notify_email || company.enquiryEmail}</strong>
              {emailInfo.email_mode === "formsubmit" ? " via FormSubmit relay. If emails are not arriving, open that inbox (check Spam) and click the one-time \"Activate Form\" link." : " via Gmail."}
              {" "}Rows marked <em>emailed</em> were delivered successfully.
            </p>
          </div>
        ) : (
          <div className="mb-6 flex items-start gap-3 bg-[#fff8e6] border border-[#f3dfa3] text-[#7a5a00] rounded-md px-4 py-3 text-[13.5px]" data-testid="email-not-configured">
            <MailWarning size={18} className="mt-0.5 shrink-0" />
            <p>Email notifications are not active yet. Enquiries are safely stored here.</p>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Inbox} label="Total" value={stats.total} tone="bg-[#231f20]/5 text-[#231f20]" />
          <StatCard icon={Mail} label="New" value={stats.new} tone="bg-[#e8f5d5] text-[#4f8a12]" />
          <StatCard icon={Clock} label="Contacted" value={stats.contacted} tone="bg-[#e6f3fa] text-[#1c6f9c]" />
          <StatCard icon={CheckCircle2} label="Closed" value={stats.closed} tone="bg-[#f1f1f1] text-[#58595b]" />
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" role="tablist">
            {["all", "new", "contacted", "closed"].map((f) => (
              <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} className={`filter-pill capitalize ${filter === f ? "is-active" : ""}`} data-testid={`admin-filter-${f}`}>
                {f}
              </button>
            ))}
          </div>
          <div className="relative md:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a8a]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, message" className="field field--light pl-9" data-testid="admin-search" />
          </div>
        </div>

        <div className="mt-5 bg-white border border-[#ececec] rounded-md shadow-sm overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="p-12 text-center text-[#6d6e71]"><Loader2 className="animate-spin mx-auto mb-3" /> Loading enquiries...</div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center text-[#6d6e71]" data-testid="admin-empty">
              <Inbox size={34} className="mx-auto mb-3 text-[#c9c9c9]" />
              No enquiries found{filter !== "all" ? ` with status "${filter}"` : ""}.
            </div>
          ) : (
            <ul className="divide-y divide-[#f0f0f0]" data-testid="admin-enquiry-list">
              {visible.map((e) => (
                <li key={e.id} className="p-5 grid lg:grid-cols-12 gap-4 hover:bg-[#fafafa] transition-colors" data-testid={`enquiry-row-${e.id}`}>
                  <div className="lg:col-span-3">
                    <div className="font-bold text-[#231f20] text-[15px]">{e.name}</div>
                    {e.company && <div className="text-[12.5px] text-[#6d6e71]">{e.company}</div>}
                    <div className="mt-2 space-y-1 text-[13px]">
                      <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 text-[#4d4d4f] hover:text-[#7ab929] break-all"><Mail size={13} className="shrink-0" /> {e.email}</a>
                      <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 text-[#4d4d4f] hover:text-[#7ab929]"><Phone size={13} className="shrink-0" /> {e.phone}</a>
                    </div>
                  </div>
                  <div className="lg:col-span-6">
                    {e.enquiry_type === "quote" && (
                      <div className="mb-2 flex flex-wrap items-center gap-2" data-testid={`enquiry-quote-${e.id}`}>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#fff1e6] text-[#b5442a] border border-[#f5cdb8] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">Quote Request</span>
                        <span className="text-[13.5px] font-bold text-[#231f20]">{e.product}</span>
                        <span className="text-[13px] text-[#4d4d4f]">&middot; {e.quantity} {e.unit}</span>
                        {e.shade && <span className="text-[13px] text-[#4d4d4f]">&middot; Shade: {e.shade}</span>}
                        {e.print_process && <span className="text-[13px] text-[#4d4d4f]">&middot; {e.print_process}</span>}
                        {e.substrate && <span className="text-[13px] text-[#4d4d4f]">&middot; {e.substrate}</span>}
                      </div>
                    )}
                    {e.message ? (
                      <p className="text-[14px] leading-6 text-[#4d4d4f] whitespace-pre-line">{e.message}</p>
                    ) : (
                      <p className="text-[13px] italic text-[#9a9a9a]">No additional notes</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-[#8a8a8a]">
                      <span>{fmtDate(e.created_at)}</span>
                      <span>&middot;</span>
                      <span className="capitalize">via {e.source || "website"} form</span>
                      {e.emailed && <><span>&middot;</span><span className="text-[#4f8a12]">emailed</span></>}
                    </div>
                  </div>
                  <div className="lg:col-span-3 flex lg:flex-col items-start lg:items-end justify-between gap-3">
                    <Badge variant="outline" className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${statusMeta[e.status].cls}`} data-testid={`enquiry-status-${e.id}`}>
                      {statusMeta[e.status].label}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                        <SelectTrigger className="h-9 w-[150px] text-[13px] bg-white" data-testid={`enquiry-status-select-${e.id}`}>
                          <SelectValue placeholder="Update status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Mark as New</SelectItem>
                          <SelectItem value="contacted">Mark as Contacted</SelectItem>
                          <SelectItem value="closed">Mark as Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <button type="button" onClick={() => setToDelete(e)} className="h-9 w-9 rounded border border-[#e5e5e5] flex items-center justify-center text-[#8a8a8a] hover:text-[#c0392b] hover:border-[#c0392b] transition-colors" aria-label="Delete enquiry" data-testid={`enquiry-delete-${e.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              The enquiry from <strong>{toDelete?.name}</strong> will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#c0392b] hover:bg-[#a53125]" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  return (
    <>
      <Seo {...pageSeo.admin} />
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onSuccess={setToken} />}
    </>
  );
};

export default Admin;
